import { c as _c } from "react/compiler-runtime";
import { Product, useGcssProductList } from '@/api/skippay/gcssProducts';
import { useQueryStates, parseAsInteger } from 'nuqs';
import { parseAsSearchString, parseAsFilterArray, parseAsProductSort, parseAsAvailability, parseAsBooleanString } from '@/hooks/searchParamParsers';
import { useBrands } from '@/api/skippay/gcssBrands';
import { useMerchants } from '@/api/skippay/gcssMerchants';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from 'react-use';
import { logProductsEvent } from '@/analytics/logProductsEvent';
import { FormApi, createForm } from 'final-form';
import { FIELD_AVAILABILITY, FIELD_BRAND, FIELD_FULLTEXT, FIELD_IS_ON_SALE, FIELD_MERCHANT, FIELD_PAGE, FIELD_PRICE_FROM, FIELD_PRICE_TO, FIELD_SORT, buildGcssSearchFilters } from '../functions/buildSearchFilters';
import type { GcssSearchFilters, SearchFormValuesGCSS } from '../functions/buildSearchFilters';
const SEARCH_DEBOUNCE_MS = 400;
export { FIELD_AVAILABILITY, FIELD_BRAND, FIELD_FULLTEXT, FIELD_IS_ON_SALE, FIELD_MERCHANT, FIELD_PAGE, FIELD_PRICE_FROM, FIELD_PRICE_TO, FIELD_SORT };
export type { SearchFormValuesGCSS };
export function useGcssValuesToSearch() {
  const $ = _c(27);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      search: parseAsSearchString,
      page: parseAsInteger.withDefault(1).withOptions({
        history: "replace",
        shallow: true,
        throttleMs: 300
      }),
      sort: parseAsProductSort,
      availability: parseAsAvailability,
      priceFrom: parseAsSearchString,
      priceTo: parseAsSearchString,
      brand: parseAsFilterArray,
      isOnSale: parseAsBooleanString,
      merchant: parseAsFilterArray
    };
    t1 = {
      history: "replace",
      shallow: true
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  const [searchParams, setSearchParams] = useQueryStates(t0, t1);
  const t2 = searchParams.search || undefined;
  let t3;
  if ($[2] !== searchParams.page) {
    t3 = searchParams.page?.toString() || "1";
    $[2] = searchParams.page;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = searchParams.sort || undefined;
  const t5 = searchParams.availability || undefined;
  const t6 = searchParams.priceFrom || undefined;
  const t7 = searchParams.priceTo || undefined;
  const t8 = searchParams.brand && searchParams.brand.length > 0 ? searchParams.brand : undefined;
  const t9 = searchParams.isOnSale || undefined;
  const t10 = searchParams.merchant && searchParams.merchant.length > 0 ? searchParams.merchant : undefined;
  let t11;
  if ($[4] !== t10 || $[5] !== t2 || $[6] !== t3 || $[7] !== t4 || $[8] !== t5 || $[9] !== t6 || $[10] !== t7 || $[11] !== t8 || $[12] !== t9) {
    t11 = {
      [FIELD_FULLTEXT]: t2,
      [FIELD_PAGE]: t3,
      [FIELD_SORT]: t4,
      [FIELD_AVAILABILITY]: t5,
      [FIELD_PRICE_FROM]: t6,
      [FIELD_PRICE_TO]: t7,
      [FIELD_BRAND]: t8,
      [FIELD_IS_ON_SALE]: t9,
      [FIELD_MERCHANT]: t10
    };
    $[4] = t10;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  const formValues = t11;
  let t12;
  if ($[14] !== setSearchParams) {
    t12 = values => {
      setSearchParams({
        search: values[FIELD_FULLTEXT] || null,
        page: values[FIELD_PAGE] ? parseInt(values[FIELD_PAGE]) : null,
        sort: values[FIELD_SORT] || null,
        availability: values[FIELD_AVAILABILITY] || null,
        priceFrom: values[FIELD_PRICE_FROM] || null,
        priceTo: values[FIELD_PRICE_TO] || null,
        brand: values[FIELD_BRAND] && values[FIELD_BRAND].length > 0 ? values[FIELD_BRAND] : null,
        isOnSale: values[FIELD_IS_ON_SALE] || null,
        merchant: values[FIELD_MERCHANT] && values[FIELD_MERCHANT].length > 0 ? values[FIELD_MERCHANT] : null
      });
    };
    $[14] = setSearchParams;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const sync = t12;
  let t13;
  if ($[16] !== formValues || $[17] !== sync) {
    t13 = () => createForm({
      initialValues: formValues as Partial<SearchFormValuesGCSS>,
      onSubmit: values_0 => {
        sync(values_0);
      }
    });
    $[16] = formValues;
    $[17] = sync;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const [searchForm] = useState(t13);
  let t14;
  let t15;
  if ($[19] !== formValues || $[20] !== searchForm) {
    t14 = () => {
      if (typeof window !== "undefined") {
        searchForm.reset(formValues as Partial<SearchFormValuesGCSS>);
      }
    };
    t15 = [formValues, searchForm];
    $[19] = formValues;
    $[20] = searchForm;
    $[21] = t14;
    $[22] = t15;
  } else {
    t14 = $[21];
    t15 = $[22];
  }
  useEffect(t14, t15);
  let t16;
  if ($[23] !== formValues || $[24] !== searchForm || $[25] !== sync) {
    t16 = {
      formValues,
      sync,
      searchForm
    };
    $[23] = formValues;
    $[24] = searchForm;
    $[25] = sync;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  return t16;
}
export function useGcssSearch(t0) {
  const $ = _c(49);
  const {
    formValues,
    categoryId,
    category,
    subcategory
  } = t0;
  const {
    isReady
  } = useRouter();
  const fulltext = formValues[FIELD_FULLTEXT];
  const availability = formValues[FIELD_AVAILABILITY];
  const priceFrom = formValues[FIELD_PRICE_FROM];
  const priceTo = formValues[FIELD_PRICE_TO];
  const brand = formValues[FIELD_BRAND];
  const merchant = formValues[FIELD_MERCHANT];
  const isOnSale = formValues[FIELD_IS_ON_SALE];
  const sort = formValues[FIELD_SORT];
  let t1;
  if ($[0] !== availability || $[1] !== brand || $[2] !== categoryId || $[3] !== fulltext || $[4] !== isOnSale || $[5] !== merchant || $[6] !== priceFrom || $[7] !== priceTo || $[8] !== sort) {
    t1 = buildGcssSearchFilters({
      [FIELD_FULLTEXT]: fulltext,
      [FIELD_AVAILABILITY]: availability,
      [FIELD_PRICE_FROM]: priceFrom,
      [FIELD_PRICE_TO]: priceTo,
      [FIELD_BRAND]: brand,
      [FIELD_MERCHANT]: merchant,
      [FIELD_IS_ON_SALE]: isOnSale,
      [FIELD_SORT]: sort
    }, categoryId);
    $[0] = availability;
    $[1] = brand;
    $[2] = categoryId;
    $[3] = fulltext;
    $[4] = isOnSale;
    $[5] = merchant;
    $[6] = priceFrom;
    $[7] = priceTo;
    $[8] = sort;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const filters = t1;
  const [debouncedFilters, setDebouncedFilters] = useState(null);
  let t2;
  let t3;
  if ($[10] !== filters || $[11] !== isReady) {
    t2 = () => {
      if (isReady) {
        setDebouncedFilters(prev => prev ?? filters);
      }
    };
    t3 = [isReady, filters];
    $[10] = filters;
    $[11] = isReady;
    $[12] = t2;
    $[13] = t3;
  } else {
    t2 = $[12];
    t3 = $[13];
  }
  useEffect(t2, t3);
  let t4;
  if ($[14] !== filters || $[15] !== isReady) {
    t4 = () => {
      if (isReady) {
        setDebouncedFilters(filters);
      }
    };
    $[14] = filters;
    $[15] = isReady;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== filters) {
    t5 = [filters];
    $[17] = filters;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  useDebounce(t4, SEARCH_DEBOUNCE_MS, t5);
  const enabled = debouncedFilters !== null;
  const t6 = debouncedFilters?.productFilter;
  const t7 = debouncedFilters?.sort;
  let t8;
  if ($[19] !== enabled || $[20] !== t6 || $[21] !== t7) {
    t8 = {
      filter: t6,
      sort: t7,
      limit: 12,
      enabled
    };
    $[19] = enabled;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const query = useGcssProductList(t8);
  const t9 = debouncedFilters?.brandsFilter;
  let t10;
  if ($[23] !== enabled || $[24] !== t9) {
    t10 = {
      filter: t9,
      enabled
    };
    $[23] = enabled;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const brandsQuery = useBrands(t10);
  const t11 = debouncedFilters?.merchantsFilter;
  let t12;
  if ($[26] !== enabled || $[27] !== t11) {
    t12 = {
      filter: t11,
      enabled
    };
    $[26] = enabled;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const merchantsQuery = useMerchants(t12);
  const queries = [query, brandsQuery, merchantsQuery];
  let t13;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = [];
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const cssPartnersPreviousData = useRef(t13);
  let t14;
  if ($[30] !== query.data?.pages) {
    t14 = query.data?.pages.flatMap(_temp);
    $[30] = query.data?.pages;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const allCssPartners = t14;
  let t15;
  if ($[32] !== allCssPartners || $[33] !== category || $[34] !== query.data?.pages?.length || $[35] !== query.isFetching || $[36] !== subcategory) {
    t15 = () => {
      const difference = allCssPartners?.filter(partner => !cssPartnersPreviousData.current?.includes(partner));
      if (!query.isFetching && difference && difference.length > 0) {
        logProductsEvent(difference, category, subcategory, query.data?.pages?.length);
      }
      cssPartnersPreviousData.current = [...cssPartnersPreviousData.current, ...(difference || [])];
    };
    $[32] = allCssPartners;
    $[33] = category;
    $[34] = query.data?.pages?.length;
    $[35] = query.isFetching;
    $[36] = subcategory;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  const t16 = query.data?.pages?.length;
  let t17;
  if ($[38] !== allCssPartners || $[39] !== category || $[40] !== query.isFetching || $[41] !== subcategory || $[42] !== t16) {
    t17 = [allCssPartners, category, t16, query.isFetching, subcategory];
    $[38] = allCssPartners;
    $[39] = category;
    $[40] = query.isFetching;
    $[41] = subcategory;
    $[42] = t16;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  useEffect(t15, t17);
  const t18 = queries.some(_temp2);
  let t19;
  if ($[44] !== brandsQuery || $[45] !== merchantsQuery || $[46] !== query || $[47] !== t18) {
    t19 = {
      query,
      brandsFilterResult: brandsQuery,
      merchantsFilterResult: merchantsQuery,
      isPending: t18
    };
    $[44] = brandsQuery;
    $[45] = merchantsQuery;
    $[46] = query;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  return t19;
}
function _temp2(q) {
  return q.isPending;
}
function _temp(subArray) {
  return subArray.data;
}